import type {
  CoachingGoal,
  CoachingGoalMilestone,
  CoachingPlan,
} from '@efdigital/ll-addon-data';
import { DataTypeCoaching } from '@efdigital/ll-addon-data/data-types';
import GoalDeleteModal from '@efdigital/ll-xceed-v2/components/coaching/goal-delete-modal.gts';
import { asAsyncBelongsTo } from '@efdigital/ll-xceed-v2/data/async-setter';
import {
  createCoachingGoalMilestone,
  deleteCoachingGoalMilestone,
  updateCoachingGoalMilestone,
} from '@efdigital/ll-xceed-v2/data/builders/coaching/coaching-goal-milestones';
import {
  createCoachingGoal,
  deleteCoachingGoal,
  updateCoachingGoal,
} from '@efdigital/ll-xceed-v2/data/builders/coaching/coaching-goals';
import { saveFailureMessage } from '@efdigital/ll-xceed-v2/utils/application/request-error';
import { milestonesForGoal } from '@efdigital/ll-xceed-v2/utils/coaching/goal-children';
import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import type RouterService from '@ember/routing/router-service';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { cached, tracked } from '@glimmer/tracking';
import type { Store } from '@warp-drive/core';

interface GoalCreateFormSignature {
  Args: {
    plan?: CoachingPlan | null;
    ownerUserId?: string;
    goal?: CoachingGoal;
    milestones: readonly CoachingGoalMilestone[];
    onCreated?: () => void;
    onClose?: () => void;
    onDeleted?: () => void;
  };
  Element: HTMLFormElement;
}

function gt(left: number, right: number): boolean {
  return left > right;
}

function dateInputValue(iso: string | null | undefined): string {
  if (!iso) {
    return '';
  }
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return '';
  }
  return date.toISOString().slice(0, 10);
}

interface MilestoneField {
  key: string;
  record?: CoachingGoalMilestone;
  title: string;
}

const MAX_MILESTONES = 10;
let nextMilestoneKey = 0;

function createMilestoneField(
  title = '',
  record?: CoachingGoalMilestone
): MilestoneField {
  nextMilestoneKey += 1;
  return { key: String(nextMilestoneKey), record, title };
}

export default class GoalCreateForm extends Component<GoalCreateFormSignature> {
  @service declare store: Store;
  @service declare router: RouterService;

  @tracked title = this.args.goal?.title ?? '';
  @tracked detail = this.args.goal?.detail ?? '';
  @tracked targetDate = dateInputValue(this.args.goal?.targetDate);
  @tracked milestones: MilestoneField[] = this.seedMilestones();
  @tracked saving = false;
  @tracked deleting = false;
  @tracked errorMessage: string | null = null;
  @tracked titleError: string | null = null;
  @tracked targetDateError: string | null = null;

  get isEditing(): boolean {
    return Boolean(this.args.goal);
  }

  get titleErrorId(): string {
    return `${this.formId}-title-error`;
  }

  get targetDateErrorId(): string {
    return `${this.formId}-target-date-error`;
  }

  /**
   * The form is `novalidate` so that a half-typed native date does not silently
   * abort submit: an incomplete `<input type="date">` reports an empty value
   * with `badInput`, which browsers block without ever running our handler and
   * makes an optional field look mandatory.
   */
  validate(form: HTMLFormElement): boolean {
    this.titleError = this.title.trim() ? null : 'Enter a title for this goal.';

    const targetDate = form.elements.namedItem(
      'targetDate'
    ) as HTMLInputElement | null;
    this.targetDateError = targetDate?.validity.badInput
      ? 'Enter a complete date, or clear the field to leave the target date out.'
      : null;

    return !this.titleError && !this.targetDateError;
  }

  get formId(): string {
    return this.args.goal?.id
      ? `goal-edit-${this.args.goal.id}`
      : 'goal-create';
  }

  seedMilestones(): MilestoneField[] {
    const goalId = this.args.goal?.id;
    if (!goalId) {
      return [createMilestoneField()];
    }

    const existing = milestonesForGoal(this.args.milestones, goalId);
    if (!existing.length) {
      return [createMilestoneField()];
    }

    return existing.map((milestone) =>
      createMilestoneField(milestone.title, milestone)
    );
  }

  @cached
  get milestoneCountLabel(): string {
    const count = this.milestones.length;

    return `${count}/${MAX_MILESTONES} milestones`;
  }

  get canAddMilestone(): boolean {
    return this.milestones.length < MAX_MILESTONES;
  }

  milestoneNumber = (index: number): number => index + 1;

  resetForm = () => {
    this.title = '';
    this.detail = '';
    this.targetDate = '';
    this.milestones = [createMilestoneField()];
    this.errorMessage = null;
    this.titleError = null;
    this.targetDateError = null;
  };

  close = () => {
    this.resetForm();
    this.args.onClose?.();
  };

  refreshGoals = async () => {
    const routeName = this.router.currentRouteName ?? '';

    if (routeName.startsWith('coaching.coachee.cohort.goals')) {
      await this.router.refresh('coaching.coachee.cohort.goals');
      return;
    }

    await this.router.refresh();
  };

  reconcileGoals = async () => {
    try {
      await this.refreshGoals();
    } catch (error) {
      console.error(error);
    }
  };

  addMilestone = () => {
    if (!this.canAddMilestone) {
      return;
    }

    this.milestones = [...this.milestones, createMilestoneField()];
  };

  removeMilestone = (milestone: MilestoneField) => {
    this.milestones = this.milestones.filter((field) => field !== milestone);
  };

  setTitle = (event: Event) => {
    this.title = (event.target as HTMLInputElement).value;
    this.titleError = null;
  };

  setDetail = (event: Event) => {
    this.detail = (event.target as HTMLTextAreaElement).value;
  };

  setTargetDate = (event: Event) => {
    const input = event.target as HTMLInputElement;
    this.targetDate = input.value;
    if (!input.validity.badInput) {
      this.targetDateError = null;
    }
  };

  setMilestone = (milestone: MilestoneField, event: Event) => {
    const title = (event.target as HTMLInputElement).value;
    this.milestones = this.milestones.map((field) =>
      field === milestone ? { ...field, title } : field
    );
  };

  create = async (event: Event) => {
    event.preventDefault();
    if (this.saving) {
      return;
    }

    if (!this.validate(event.currentTarget as HTMLFormElement)) {
      return;
    }

    if (this.isEditing) {
      await this.saveEdit();
      return;
    }

    const title = this.title.trim();
    const plan = this.args.plan;
    const ownerUserId = this.args.ownerUserId;
    if (!plan || !ownerUserId) {
      this.errorMessage =
        'This goal has no coaching engagement to attach to, so it cannot be saved.';
      return;
    }

    this.saving = true;
    this.errorMessage = null;

    const created = this.store.createRecord<CoachingGoal>(
      DataTypeCoaching.CoachingGoal,
      {
        coachingPlan: asAsyncBelongsTo(plan),
        user: ownerUserId,
        title,
        detail: this.detail.trim(),
        status: 'active',
        targetDate: this.targetDate
          ? new Date(`${this.targetDate}T00:00:00.000Z`).toISOString()
          : null,
      }
    );

    const milestoneDrafts: CoachingGoalMilestone[] = [];
    const persistedMilestones: CoachingGoalMilestone[] = [];

    try {
      await this.store.request(createCoachingGoal(created, this.store));

      try {
        await this.createMilestones(
          created,
          milestoneDrafts,
          persistedMilestones
        );
      } catch (error) {
        console.error(error);

        for (const milestone of persistedMilestones) {
          try {
            await this.store.request(deleteCoachingGoalMilestone(milestone));
          } catch (cleanupError) {
            console.error(cleanupError);
          }
        }

        try {
          await this.store.request(deleteCoachingGoal(created));
        } catch (cleanupError) {
          console.error(cleanupError);
        }

        for (const milestone of milestoneDrafts) {
          try {
            milestone.unloadRecord();
          } catch {
            // Successful cleanup may already have unloaded the milestone.
          }
        }

        try {
          created.unloadRecord();
        } catch {
          // A successful cleanup may already have unloaded the goal.
        }
        this.errorMessage =
          'The milestones could not be saved. Cleanup was attempted; please try again.';
        await this.reconcileGoals();
        return;
      }

      this.resetForm();
      this.args.onCreated?.();
      await this.refreshGoals();
    } catch (error) {
      created.unloadRecord();
      console.error(error);
      this.errorMessage = saveFailureMessage(
        error,
        'The goal could not be saved.'
      );
    } finally {
      this.saving = false;
    }
  };

  createMilestones = async (
    goal: CoachingGoal,
    createdDrafts: CoachingGoalMilestone[] = [],
    persistedMilestones: CoachingGoalMilestone[] = []
  ) => {
    const fields = this.milestones.filter((field) => field.title.trim());

    for (const [position, field] of fields.entries()) {
      const milestone = this.store.createRecord<CoachingGoalMilestone>(
        DataTypeCoaching.CoachingGoalMilestone,
        {
          coachingGoal: asAsyncBelongsTo(goal),
          title: field.title.trim(),
          completed: false,
          position,
        }
      );
      createdDrafts.push(milestone);
      await this.store.request(createCoachingGoalMilestone(milestone, goal));
      persistedMilestones.push(milestone);
    }
  };

  synchronizeMilestones = async (
    goal: CoachingGoal,
    createdDrafts: CoachingGoalMilestone[]
  ) => {
    const retainedRecords = new Set(
      this.milestones
        .filter((field) => field.title.trim())
        .map((field) => field.record)
        .filter((record): record is CoachingGoalMilestone =>
          Boolean(record?.id)
        )
        .map((record) => record.id)
    );

    const fields = this.milestones.filter((field) => field.title.trim());
    for (const [position, field] of fields.entries()) {
      const title = field.title.trim();
      if (!field.record) {
        const milestone = this.store.createRecord<CoachingGoalMilestone>(
          DataTypeCoaching.CoachingGoalMilestone,
          {
            coachingGoal: asAsyncBelongsTo(goal),
            title,
            completed: false,
            position,
          }
        );
        createdDrafts.push(milestone);
        await this.store.request(createCoachingGoalMilestone(milestone, goal));
        continue;
      }

      const attributes: Partial<
        Pick<CoachingGoalMilestone, 'title' | 'position'>
      > = {};
      if (field.record.title !== title) {
        attributes.title = title;
      }
      if (field.record.position !== position) {
        attributes.position = position;
      }

      if (Object.keys(attributes).length) {
        Object.assign(field.record, attributes);
        await this.store.request(
          updateCoachingGoalMilestone(field.record, attributes)
        );
      }
    }

    // Complete non-destructive writes before removing records. These requests
    // are not transactional; reconciliation below reflects any partial result.
    for (const existing of milestonesForGoal(this.args.milestones, goal.id!)) {
      if (!retainedRecords.has(existing.id)) {
        await this.store.request(deleteCoachingGoalMilestone(existing));
      }
    }
  };

  saveEdit = async () => {
    const goal = this.args.goal;
    const title = this.title.trim();
    if (this.saving || !goal || !title) {
      return;
    }

    this.saving = true;
    this.errorMessage = null;

    const previous = {
      title: goal.title,
      detail: goal.detail,
      targetDate: goal.targetDate,
    };
    const milestoneSnapshots = new Map(
      milestonesForGoal(this.args.milestones, goal.id!).map((milestone) => [
        milestone,
        {
          title: milestone.title,
          completed: milestone.completed,
          position: milestone.position,
        },
      ])
    );
    const createdDrafts: CoachingGoalMilestone[] = [];
    goal.title = title;
    goal.detail = this.detail.trim();
    goal.targetDate = this.targetDate
      ? new Date(`${this.targetDate}T00:00:00.000Z`).toISOString()
      : null;

    try {
      await this.store.request(updateCoachingGoal(goal));
      await this.synchronizeMilestones(goal, createdDrafts);
    } catch (error) {
      console.error(error);
      goal.rollbackAttributes();
      Object.assign(goal, previous);
      for (const [milestone, snapshot] of milestoneSnapshots) {
        try {
          milestone.rollbackAttributes();
          Object.assign(milestone, snapshot);
        } catch {
          // A successful DELETE may already have unloaded this record.
        }
      }
      for (const milestone of createdDrafts) {
        try {
          milestone.unloadRecord();
        } catch {
          // A later failed operation may follow an already-reconciled create.
        }
      }
      this.errorMessage = saveFailureMessage(
        error,
        'The goal changes could not be saved. The latest server state is being restored.'
      );
      await this.reconcileGoals();
      goal.rollbackAttributes();
      for (const milestone of milestoneSnapshots.keys()) {
        try {
          milestone.rollbackAttributes();
        } catch {
          // Deleted records are absent from the refreshed server state.
        }
      }
      this.saving = false;
      return;
    }

    try {
      this.args.onCreated?.();
      await this.refreshGoals();
    } catch (error) {
      console.error(error);
      this.errorMessage =
        'The goal was saved, but the page could not be refreshed.';
    } finally {
      this.saving = false;
    }
  };

  deleteGoal = async () => {
    const goal = this.args.goal;
    if (this.deleting || !goal) {
      return;
    }

    this.deleting = true;
    this.errorMessage = null;

    try {
      await this.store.request(deleteCoachingGoal(goal));
      await this.refreshGoals();
      this.args.onDeleted?.();
    } catch (error) {
      console.error(error);
      this.errorMessage = saveFailureMessage(
        error,
        'The goal could not be deleted.'
      );
    } finally {
      this.deleting = false;
    }
  };

  <template>
    <form
      {{on "submit" this.create}}
      id={{this.formId}}
      class="card border shadow-sm rounded-4 mb-4 overflow-hidden"
      novalidate
      data-test-goal-create
      ...attributes
    >
      <div
        class="card-header bg-transparent border-bottom d-flex align-items-center justify-content-between gap-3 py-3 px-4"
      >
        <h2 class="h5 fw-bold mb-0">{{if
            this.isEditing
            "Edit goal"
            "Add a goal"
          }}</h2>
        <button
          type="button"
          class="btn-close"
          aria-label="Close"
          {{on "click" this.close}}
        ></button>
      </div>

      <div class="card-body p-4">
        <div class="mb-3">
          <label for="{{this.formId}}-title" class="form-label">Title</label>
          <input
            id="{{this.formId}}-title"
            class="form-control {{if this.titleError 'is-invalid'}}"
            type="text"
            maxlength="300"
            required
            aria-invalid={{if this.titleError "true"}}
            aria-describedby={{if this.titleError this.titleErrorId}}
            placeholder="Chair the Q3 planning session"
            value={{this.title}}
            {{on "input" this.setTitle}}
          />
          {{#if this.titleError}}
            <p
              id={{this.titleErrorId}}
              class="invalid-feedback d-block mb-0"
              data-test-goal-title-error
            >{{this.titleError}}</p>
          {{/if}}
        </div>

        <div class="mb-3">
          <label for="{{this.formId}}-detail" class="form-label">Detail
            <span
              class="text-body-secondary fw-normal"
            >(optional)</span></label>
          <textarea
            id="{{this.formId}}-detail"
            class="form-control"
            rows="3"
            maxlength="5000"
            value={{this.detail}}
            {{on "input" this.setDetail}}
          ></textarea>
        </div>

        <div class="mb-4">
          <label for="{{this.formId}}-target-date" class="form-label">Target
            date
            <span
              class="text-body-secondary fw-normal"
            >(optional)</span></label>
          <input
            id="{{this.formId}}-target-date"
            name="targetDate"
            class="form-control {{if this.targetDateError 'is-invalid'}}"
            type="date"
            {{! no aria-invalid: template-lint maps a date input to role=button, which does not support it }}
            aria-describedby={{if this.targetDateError this.targetDateErrorId}}
            value={{this.targetDate}}
            {{on "input" this.setTargetDate}}
            {{on "change" this.setTargetDate}}
          />
          {{#if this.targetDateError}}
            <p
              id={{this.targetDateErrorId}}
              class="invalid-feedback d-block mb-0"
              data-test-goal-target-date-error
            >{{this.targetDateError}}</p>
          {{/if}}
        </div>

        <div class="card bg-body border rounded-3">
          <div class="card-body p-3">
            <div
              class="d-flex align-items-start justify-content-between gap-3 mb-2"
            >
              <h3 class="h6 fw-bold mb-0">Milestones</h3>
              <p class="gud-eyebrow text-body-secondary mb-0">
                {{this.milestoneCountLabel}}
              </p>
            </div>
            <p class="small text-body-secondary mb-3">Break this goal into steps
              you can check off as you go.</p>

            {{#each this.milestones key="key" as |milestone index|}}
              <div class="d-flex align-items-center gap-2 mb-2 min-w-0">
                <span
                  class="ll-avatar-xs rounded-circle border bg-body d-inline-flex align-items-center justify-content-center flex-shrink-0 text-body-secondary fw-semibold small"
                  aria-hidden="true"
                >{{this.milestoneNumber index}}</span>
                <label
                  for="goal-milestone-{{milestone.key}}"
                  class="visually-hidden"
                >Milestone {{this.milestoneNumber index}}</label>
                <input
                  id="goal-milestone-{{milestone.key}}"
                  class="form-control flex-grow-1 min-w-0"
                  type="text"
                  maxlength="300"
                  placeholder="Milestone title"
                  value={{milestone.title}}
                  {{on "input" (fn this.setMilestone milestone)}}
                />
                {{#if (gt this.milestones.length 1)}}
                  <button
                    type="button"
                    class="btn btn-outline-primary flex-shrink-0"
                    aria-label="Remove milestone"
                    {{on "click" (fn this.removeMilestone milestone)}}
                  >
                    <iconify-icon inline icon="ph:trash" aria-hidden="true" />
                  </button>
                {{/if}}
              </div>
            {{/each}}

            {{#if this.canAddMilestone}}
              <button
                type="button"
                class="btn btn-outline-primary btn-sm"
                {{on "click" this.addMilestone}}
              >
                <iconify-icon inline icon="ph:plus" aria-hidden="true" />
                Add milestone
              </button>
            {{else}}
              <p class="small text-body-secondary mb-0">
                Maximum of
                {{MAX_MILESTONES}}
                milestones reached.
              </p>
            {{/if}}
          </div>
        </div>

        {{#if this.errorMessage}}
          <p
            class="text-danger small mt-3 mb-0"
            role="alert"
          >{{this.errorMessage}}</p>
        {{/if}}
      </div>

      <div
        class="card-footer bg-transparent border-top d-flex flex-column flex-sm-row align-items-stretch align-items-sm-center justify-content-between gap-3 py-3 px-4"
      >
        {{#if this.isEditing}}
          <GoalDeleteModal
            @deleting={{this.deleting}}
            @onConfirm={{this.deleteGoal}}
            as |showDeleteModal|
          >
            <button
              type="button"
              class="btn btn-outline-primary"
              disabled={{this.deleting}}
              data-test-goal-delete
              {{on "click" showDeleteModal}}
            >
              <iconify-icon inline icon="ph:trash" aria-hidden="true" />
              Delete
            </button>
          </GoalDeleteModal>
        {{else}}
          <p class="small text-body-secondary mb-0">Only the title is needed to
            start.</p>
        {{/if}}
        <div class="d-grid d-sm-flex align-items-center ms-sm-auto">
          <button
            type="submit"
            class="btn btn-primary"
            disabled={{this.saving}}
          >{{if
              this.saving
              "Saving…"
              (if this.isEditing "Save goal" "Add goal")
            }}</button>
        </div>
      </div>
    </form>
  </template>
}
